import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'out-thumbnails',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './thumbnail.component.html',
  styleUrls: ['./thumbnail.component.scss'],
})
export class ThumbnailsComponent implements AfterViewInit, OnDestroy {
  @Input() images: any[] = [];
  @Input() index = 0;
  @Output() indexChange = new EventEmitter<number>();
  @Input() loop = false;

  // Optional outputs for previous/next controls if needed:
  @Output() prevEvent = new EventEmitter<number>();
  @Output() nextEvent = new EventEmitter<number>();

  @ViewChild('thumbs', { static: true }) thumbsRef!: ElementRef;
  @ViewChild('thumbItems', { read: ElementRef }) thumbItemsRef!: ElementRef;

  slide = 'next';
  swipeDone = false;
  swipeX: number | null = null;

  resizeSub!: Subscription;

  constructor(private cd: ChangeDetectorRef) {}

  get prevImage(): number {
    if (this.index > 0) {
      return this.index - 1;
    }
    return this.loop ? this.images.length - 1 : this.index;
  }

  get nextImage(): number {
    if (this.index < this.images.length - 1) {
      return this.index + 1;
    }
    return this.loop ? 0 : this.index;
  }

  isVimeo(image: any): boolean {
    const src = typeof image === 'string' ? image : image.src || '';
    return src.includes('vimeo.com') || src.includes('vimeocdn.com');
  }

  ngAfterViewInit(): void {
    this.adjustThumbnailsScroll(this.index);
    this.resizeSub = fromEvent(window, 'resize').subscribe(() => {
      this.handleResize();
    });
    // In case layout changes after view init:
    setTimeout(() => this.adjustThumbnailsScroll(this.index), 500);
  }

  ngOnDestroy(): void {
    if (this.resizeSub) {
      this.resizeSub.unsubscribe();
    }
  }

  adjustThumbnailsScroll(newIndex: number): void {
    // Use setTimeout to allow view updates
    setTimeout(() => {
      const thumbs: HTMLElement = this.thumbsRef.nativeElement;
      // We assume each thumb item is a child of thumbs.
      const thumbItems = thumbs.children;
      if (thumbItems[newIndex]) {
        this.calculateScroll(thumbs, thumbItems[newIndex] as HTMLElement);
      }
    }, 100);
  }

  calculateScroll(thumbs: HTMLElement, curThumb: HTMLElement): void {
    if (
      curThumb.offsetLeft + curThumb.clientWidth / 2 >
      window.innerWidth / 2
    ) {
      const distance =
        curThumb.offsetLeft - window.innerWidth / 2 + curThumb.clientWidth / 2;
      thumbs.scrollLeft =
        distance < thumbs.scrollWidth ? distance : thumbs.scrollWidth;
    } else {
      thumbs.scrollLeft = 0;
    }
  }

  prev(): void {
    this.prevEvent.emit(this.prevImage);
    this.goto(this.prevImage, 'prev');
  }

  next(): void {
    this.nextEvent.emit(this.nextImage);
    this.goto(this.nextImage, 'next');
  }

  goto(idx: number, slide = 'next'): void {
    this.slide = this.index < idx ? 'next' : 'prev';
    this.index = idx;
    this.indexChange.emit(this.index);
    this.adjustThumbnailsScroll(this.index);
    this.cd.markForCheck();
  }

  handleResize(): void {
    this.adjustThumbnailsScroll(this.index);
  }

  // Optional swipe support if needed
  swipeStart(e: TouchEvent): void {
    this.swipeX = e.changedTouches[0].screenX;
  }

  swipe(e: TouchEvent): void {
    const swipeDistance = e.changedTouches[0].screenX - (this.swipeX || 0);
    if (swipeDistance >= 50) {
      this.prev();
    } else if (swipeDistance <= -50) {
      this.next();
    }
  }

  // Optional keyup handler if you wish to add key navigation
  keyup(e: KeyboardEvent): void {
    if (e.key === 'ArrowRight') {
      this.next();
    } else if (e.key === 'ArrowLeft') {
      this.prev();
    } else if (e.key === 'Escape') {
      // Implement close functionality if needed.
    }
  }
}
